import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AssetTypeEnum } from 'src/app/enums/assetType.enum';
import { ToastService } from 'src/app/services/toast.service';
import { UploadService } from 'src/app/services/upload.service';
import { generateGUID } from 'src/app/_helpers/guid.helper';
import { FileAttachmentService } from 'src/app/services/file-attachment.service';
import { FileAttachment } from 'src/app/models/fileAttachment.model';
import { CreatePayload } from 'src/app/shared/grid/crud.service';
import { FileTypeEnum } from 'src/app/enums/fileType.enum';
import { Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { DocumentType } from 'src/app/enums/documentType.enum';

export interface IAttachmentsFormDialogData {
  allowedFileTypes?: string[];
  maxFileSizeMB?: number;
  assetType: AssetTypeEnum;
  organizationLevelId?: string;
  showDescription?: boolean;
  attachFunction?: (payload) => Observable<any>;
}

interface UploadedFile {
  name: string;
  size: number;
  progress: number;
  uploading: boolean;
  url?: string;
  file: File;
}

@Component({
  standalone: false,
  selector: 'app-attachments-dialog',
  templateUrl: './attachments-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./attachments-dialog.component.scss'],
})
export class AttachmentsDialogComponent {
  form: FormGroup;
  uploadedFile: UploadedFile | null = null;
  isLoading: boolean = false;
  fileKey: string | null = null;
  showDescription: boolean;

  constructor(
    private fb: FormBuilder,
    private uploadService: UploadService,
    private fileAttachmentService: FileAttachmentService,
    public dialogRef: MatDialogRef<AttachmentsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IAttachmentsFormDialogData,
    private toastService: ToastService
  ) {
    this.form = this.fb.group({
      fileName: ['', { disabled: true }],
      description: [''],
    });
    this.showDescription = !!this.data.showDescription;
  }

  onFileSelected(event: Event | FileList): void {
    const files = event instanceof FileList ? event : (event.target as HTMLInputElement)?.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!this.isFileValid(file)) return;
    this.fileKey = generateGUID();

    this.uploadedFile = {
      name: file.name,
      size: file.size,
      progress: 0,
      uploading: false,
      file,
    };

    this.form.get('fileName')?.setValue(file.name);
  }

  save(): void {
    if (!this.uploadedFile || !this.fileKey) {
      this.toastService.displayErrorMessage('No file selected', true);
      return;
    }

    if (!this.form.get('fileName')?.value?.trim()) {
      this.toastService.displayErrorMessage('File name is required', true);
      return;
    }

    this.isLoading = true;
    this.uploadedFile.uploading = true;

    this.uploadService.getAssetsPreSignedURL(this.fileKey, this.data.assetType).pipe(
      switchMap((signedUrl: string) =>
        this.uploadService.uploadFile(signedUrl, this.uploadedFile!.file).pipe(
          tap(() => {
            this.uploadedFile!.progress = 100;
            this.uploadedFile!.uploading = false;
            this.uploadedFile!.url = signedUrl.split('?')[0];
          })
        )
      )
    ).subscribe({
      next: () => this.attachFile(),
      error: () => {
        this.uploadedFile!.uploading = false;
        this.isLoading = false;
        this.toastService.showError('Upload failed');
      }
    });
  }

  attachFile() {
    const payload: CreatePayload<FileAttachment> = {
      documentType: DocumentType.FieldSheet,
      name: this.form.get('fileName')?.value,
      fileKey: this.fileKey,
      fileSizeInBytes: this.uploadedFile?.file.size,
      description: this.form.get('description')?.value ?? '',
      organizationLevelId: this.data.organizationLevelId,
      fileType: this.getFileType(this.uploadedFile?.file.name || '')
    };
    if (!this.showDescription) {
      delete payload.description;
    }
    const upload$ = this.data?.attachFunction
      ? this.data.attachFunction(payload) : this.fileAttachmentService.create(payload);

    upload$.subscribe({
      next: (res) => {
        this.toastService.showSuccess('File Attached Successfully', true);
        this.isLoading = false;
        this.dialogRef.close(res);
      },
      error: (error) => {
        this.isLoading = false;
        this.toastService.showError(error);
      }
    });
  }

  isFileValid(file: File): boolean {
    const maxSize = (this.data?.maxFileSizeMB ?? 1) * 1024 * 1024;
    const allowedTypes = this.data?.allowedFileTypes ?? ['jpg', 'jpeg', 'png', 'pdf'];

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!allowedTypes.includes(ext!)) {
      this.toastService.displayErrorMessage('Invalid file type: {{ext}}', true, { ext: ext });
      return false;
    }

    if (file.size > maxSize) {
      this.toastService.displayErrorMessage('File size exceeds {{maxFileSizeMB}}MB limit', true, { maxFileSizeMB: this.data.maxFileSizeMB });
      return false;
    }

    return true;
  }

  removeFile(): void {
    this.uploadedFile = null;
    this.fileKey = null;
  }

  private getFileType(fileName: string): FileTypeEnum {
    const ext = fileName.split('.').pop()?.toLowerCase();

    if (!ext) return FileTypeEnum.Other;

    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'];
    const pdfExts = ['pdf'];
    const videoExts = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm'];
    const audioExts = ['mp3', 'wav', 'ogg', 'flac', 'm4a'];
    const docExts = ['doc', 'docx', 'txt', 'rtf', 'odt'];
    const spreadsheetExts = ['xls', 'xlsx', 'csv', 'ods'];
    const presentationExts = ['ppt', 'pptx', 'odp'];
    const archiveExts = ['zip', 'rar', '7z', 'tar', 'gz'];
    const htmlExts = ['html', 'htm'];

    if (pdfExts.includes(ext)) return FileTypeEnum.Pdf;
    if (imageExts.includes(ext)) return FileTypeEnum.Image;
    if (videoExts.includes(ext)) return FileTypeEnum.Video;
    if (audioExts.includes(ext)) return FileTypeEnum.Audio;
    if (docExts.includes(ext)) return FileTypeEnum.Document;
    if (spreadsheetExts.includes(ext)) return FileTypeEnum.Spreadsheet;
    if (presentationExts.includes(ext)) return FileTypeEnum.Presentation;
    if (archiveExts.includes(ext)) return FileTypeEnum.Archive;
    if (htmlExts.includes(ext)) return FileTypeEnum.Html;

    return FileTypeEnum.Other;
  }

  cancel(): void {
    this.dialogRef.close();
  }
}